import React, { Fragment } from 'react';
import './BlockDiagram.css';

/** Group consecutive nodes that share a zone, keeping their indexes. */
const groupByZone = (nodes) =>
  nodes.reduce((groups, node, index) => {
    const last = groups[groups.length - 1];
    if (last && node.zone && last.zone === node.zone) {
      last.items.push({ node, index });
    } else {
      groups.push({ zone: node.zone, items: [{ node, index }] });
    }
    return groups;
  }, []);

const Edge = ({ label }) => (
  <div className="bd-edge" aria-hidden="true">
    {label && <span className="bd-edge-label">{label}</span>}
    <span className="bd-edge-line" />
  </div>
);

const Node = ({ node }) => (
  <div className="bd-node">
    <p className="bd-node-label">{node.label}</p>
    {node.sub && <p className="bd-node-sub">{node.sub}</p>}
    {node.parts && (
      <div className="bd-parts">
        {node.parts.map((part) => (
          <div className="bd-part" key={part.label}>
            <p className="bd-part-label">{part.label}</p>
            <p className="bd-node-sub">{part.sub}</p>
          </div>
        ))}
      </div>
    )}
  </div>
);

/**
 * Simplified block diagram drawn in HTML so it reflows: a row of blocks
 * on wide screens, a column on narrow ones. The chain is also written
 * out as text for screen readers.
 */
export const BlockDiagram = ({ diagram }) => {
  const groups = groupByZone(diagram.nodes);
  const spoken = diagram.nodes
    .map((n, i) => (i < diagram.edges.length ? `${n.label}, ${diagram.edges[i]}, to` : n.label))
    .join(' ');

  return (
    <figure className="bd">
      <div className="bd-chain" role="img" aria-label={spoken}>
        {groups.map((group, gi) => (
          <Fragment key={gi}>
            {gi > 0 && <Edge label={diagram.edges[group.items[0].index - 1]} />}
            <div
              className={group.zone ? 'bd-group bd-zone' : 'bd-group'}
              style={{ flexGrow: group.items.length }}
            >
              {group.zone && <span className="bd-zone-label">{group.zone}</span>}
              {group.items.map(({ node, index }, k) => (
                <Fragment key={node.label}>
                  {k > 0 && <Edge label={diagram.edges[index - 1]} />}
                  <Node node={node} />
                </Fragment>
              ))}
            </div>
          </Fragment>
        ))}
      </div>
      <figcaption className="bd-caption">
        {diagram.caption}
      </figcaption>
    </figure>
  );
};

export default BlockDiagram;
